import React, { useCallback, useRef, useState } from "react";
import MenuContent, { Menubar, MenubarItem, MenubarTrigger } from "../../../components/elements/menu";
import Modal from "../../../useOss/Modal";

import "../../../style/create-new-app-env.module.css";
import "../../../style/delete-app.module.css";
import Storage from "../../../utils/storage";
import { APP_ID_STORAGE_KEY } from "../";
import { removeMultiTenancyAppOrEnv } from "../../../api/saas/multi-tenancy/connection-uri-domain/appid/delete";
import saasAnalytics from "../saasAnalytics";

export default function DeleteAppOrEnv({ deploymentId, appName }: { deploymentId: string, appName: string }) {
    const [isModalOpen, setIsModalOpen] = useState(false);

    const isPublicApp = appName === 'public';

    if (appName === undefined) {
        return null;
    }

    return (
        <>
            <Menubar>
                <MenubarTrigger>
                    <button className="delete-app-menu-btn">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="5" viewBox="0 0 20 5" fill="none">
                            <path
                                fillRule="evenodd"
                                clipRule="evenodd"
                                d="M5 2.5C5 3.88071 3.88071 5 2.5 5C1.11929 5 0 3.88071 0 2.5C0 1.11929 1.11929 0 2.5 0C3.88071 0 5 1.11929 5 2.5ZM12.5 2.5C12.5 3.88071 11.3807 5 10 5C8.61929 5 7.5 3.88071 7.5 2.5C7.5 1.11929 8.61929 0 10 0C11.3807 0 12.5 1.11929 12.5 2.5ZM17.5 5C18.8807 5 20 3.88071 20 2.5C20 1.11929 18.8807 0 17.5 0C16.1193 0 15 1.11929 15 2.5C15 3.88071 16.1193 5 17.5 5Z"
                                fill="#959595"
                            />
                        </svg>
                    </button>
                </MenubarTrigger>
                <MenuContent>
                    <MenubarItem
                        type={`${isPublicApp ? "disabled" : "danger"}`}
                        onClick={() => {
                            saasAnalytics.deleteAppOrEnv()
                            setIsModalOpen(true)
                        }}
                    >
                        {isPublicApp ? (
                            <img src="/static/assets/dashboard/disabled-delete.svg" alt="delete" />
                        ) : (
                            <img src="/static/assets/dashboard/delete.svg" alt="delete" />
                        )}
                        Delete
                    </MenubarItem>
                </MenuContent>
            </Menubar>
            {isModalOpen ? (
                <DeleteConfirmationModal
                    appName={appName}
                    deploymentId={deploymentId}
                    modalClosed={() => setIsModalOpen(false)}
                    show={isModalOpen}
                />
            ) : null}
        </>
    );
}

type DeleteConfirmationModalProps = {
    show: boolean;
    modalClosed: () => void;
    deploymentId: string;
    appName: string
};

function DeleteConfirmationModal(props: DeleteConfirmationModalProps) {
    const { modalClosed, show, deploymentId, appName } = props;

    const [typedAppName, setTypedAppName] = useState("");
    const [message, setMessage] = useState<{
        text: string;
        type: "error" | "success" | undefined;
        serverError?: boolean;
    }>({
        text: "",
        type: undefined,
        serverError: false
    });

    const [isLoading, setIsLoading] = useState(false);

    async function deleteApp() {
        setMessage({ text: "", type: undefined, serverError: false });
        setIsLoading(true);

        if (typedAppName !== appName) {
            setMessage({
                text: "Entered App ID is incorrect, Please try again.",
                type: "error"
            });
            setIsLoading(false);
            return;
        }
        saasAnalytics.deleteAppOrEnvDeleteConfirmation()
        try {
            await removeMultiTenancyAppOrEnv({
                appId: `${appName}`,
                deploymentId
            });
            setMessage({
                text: "",
                type: "success"
            });
            Storage.setItem(APP_ID_STORAGE_KEY, "public");
            window.location.reload();
        } catch (error) {
            if (error?.response && error.response?.data && error.response.data?.err) {
                setMessage({
                    text: error.response.data.err,
                    type: "error",
                    serverError: true
                });
            }
        }
    }

    const renderBody = useCallback(() => {
        if (message.type === "success") {
            return (
                <>
                    {message.type === "success" ? (
                        <div className="notification-holder">
                            <p className="success-message">
                                <img src="static/assets/dashboard/green-check.svg" alt="tick" /> App ID:{" "}
                                <span>{appName}</span> deleted successfully!
                            </p>
                        </div>
                    ) : null}
                </>
            );
        }

        if (message.serverError === true) {
            return (
                <div>
                    <div className="server-message-container">
                        <p className="server-error-message"  >
                            <img src="static/assets/dashboard/alert.png" alt="alert-icon" />
                            <p> {message.text ? message.text : "Something went wrong, Please try again!"}</p>
                        </p>
                    </div>
                    <div className="modal-action-container">
                        <button role="button" onClick={modalClosed} className="white-button app-btn">
                            Cancel
                        </button>
                        <button className="delete-app-button" onClick={deleteApp}>
                            Try again
                        </button>
                    </div>
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="modal-body loading-container">
                    <div className="center">
                        <img className="loader" src="/static/assets/dashboard/loading.svg" alt="loading" />
                    </div>
                </div>
            );
        }
        return (
            <div className="modal-body">
                <p className="confirmation-text">
                    Please confirm by typing the App ID:{" "}
                    <span className="error-message bold">{appName}</span>. This action is irreversible.
                </p>
                <div className="modal-action-container">
                    <div className="new-app-form">
                        <input
                            required
                            value={typedAppName}
                            onChange={e => setTypedAppName(e.currentTarget.value)}
                            type="text"
                            name="appName"
                            id={`${message.type === "error" ? "danger-input" : ""}`}
                            className={"primary-input"}
                            placeholder={appName}
                        />
                        {message.type === "error" ? (
                            <p className="error-message">
                                <img src="static/assets/dashboard/alert.png" alt="err-icon" />
                                {message.text}
                            </p>
                        ) : null}
                        <div className="modal-action-container">
                            <button onClick={() => {
                                saasAnalytics.deleteAppOrEnvModalCancel()
                                modalClosed()
                            }} className="white-button app-btn">
                                Cancel
                            </button>
                            <button onClick={deleteApp} className="delete-app-button">
                                Yes, Delete
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }, [isLoading, message, typedAppName]);

    return (
        <Modal disableBackdropClose={true} show={show} modalClosed={modalClosed}>
            <div className="modal-container">
                <div className="modal-header">
                    <h2>
                        {message.serverError
                            ? "Failed"
                            : isLoading
                                ? "In Process"
                                : message.type === "success"
                                    ? "App Deleted"
                                    : "Delete confirmation"}
                    </h2>
                    <span className="close-btn" onClick={() => {
                        saasAnalytics.deleteAppOrEnvModalClose()
                        modalClosed()
                    }}>
                        <img src="static/assets/dashboard/close-rounded.png" />
                    </span>
                </div>
                {renderBody()}
            </div>
        </Modal>
    );
}
